<template>
  <v-container fluid class="driver-share-wrapper pa-0">
    <v-row no-gutters class="fill-height">

      <!-- ================= LEFT PANEL ================= -->
      <v-col class="left-panel-custom">
        <div class="pa-4">

          <!-- Search -->
          <v-text-field v-model="search_driver" dense solo clearable hide-details label="Search Drivers" />

          <!-- Driver List -->
          <v-list dense class="mt-4">
            <v-list-item v-for="driver in driverListSearched" :key="driver.id" @click="toggleDriverFilter(driver)"
              :class="{
                'grey lighten-4': selectedDriver && selectedDriver.id === driver.id,
                'blue--text font-weight-bold': driver.id < 0
              }">
              <v-list-item-content>
                <v-list-item-title>{{ driver.full_name }}</v-list-item-title>
                <v-list-item-subtitle v-if="driver.id > 0">{{ driver.phone }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

        </div>
      </v-col>

      <!-- ================= RIGHT PANEL ================= -->
      <v-col class="right-panel-custom">

        <!-- ======= TOP TOOLBAR ======= -->
        <v-toolbar flat class="top-toolbar mb-3">
          <v-row align="center" class="flex-grow-1">

            <!-- LEFT SIDE -->
            <v-col cols="12" md="6" class="d-flex align-center">
               <!-- RESTAURANT GROUP (AUTOCOMPLETE) -->
              <v-autocomplete v-model="selectedRestaurantGroupId" :items="restaurantGroups" item-text="name" item-value="id" label="All locations" dense solo clearable hide-details class="location-autocomplete mr-2">
                <template slot="selection" slot-scope="data">
                  <div>
                    <div v-html="data.item.name" />
                  </div>
                </template>
                <template slot="item" slot-scope="data">
                  <div class="select-drop-item-sub_restaurant">
                    <div v-html="data.item.name" />
                  </div>
                </template>
              </v-autocomplete>
              <div class="date-pill d-flex align-center">
                <v-btn icon small @click="onPreviosRange">
                  <v-icon small color="primary">mdi-chevron-left</v-icon>
                </v-btn>

                <v-menu v-model="menu_date_picker" transition="scale-transition" :close-on-content-click="false"
                  offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn text v-on="on" class="font-weight-medium">
                      {{ dateRangeText(date_range_selected) }}
                    </v-btn>
                  </template>

                  <v-card max-width="346px">
                    <v-date-picker v-model="date_range_selected" range scrollable full-width first-day-of-week="1"
                      :title-date-format="dateRangeText" @click:date="dateRangeSelect" />
                  </v-card>
                </v-menu>

                <v-btn icon small @click="onNextRange">
                  <v-icon small color="primary">mdi-chevron-right</v-icon>
                </v-btn>
              </div>

              <v-select v-model="interval_selected_id" :items="interval_list" item-text="name" item-value="id" dense
                solo flat hide-details prepend-inner-icon="mdi-calendar-blank-outline" @change="onSelectInterval"
                class="interval-select ml-4" />
            </v-col>

            <v-col cols="12" md="6" class="d-flex align-center justify-end">

              <drivershare-copy-schedule-modal :shiftGroups="drivershareShifts" :intervalId="interval_selected_id"
                :dateRangeSelected="date_range_selected || []" @copied="onCopyCompleted" />

              <v-btn color="primary" class="rounded-pill px-6 ml-3" elevation="0" :disabled="unpublishedCount === 0"
                @click="publishAllShifts">
                <v-icon small left>mdi-upload</v-icon>
                Publish ({{ unpublishedCount }})
              </v-btn>
            </v-col>

          </v-row>
        </v-toolbar>

        <!-- ======= DAYS HEADER ======= -->
        <div class="calendar-header d-flex">
          <div v-for="col in calendarColumns" :key="col.date" class="calendar-day text-center"
            :class="{ 'calendar-day--today': col.isToday }">
            <div class="calendar-day-name">{{ col.dayName }}</div>
            <div class="calendar-day-date">{{ col.dateLabel }}</div>
          </div>
        </div>

        <!-- ======= SCROLLABLE BODY ======= -->
        <div class="calendar-body">

          <!-- ======= RESTAURANT GROUPS ======= -->
          <div v-for="restaurantGroup in restaurantGroupsWithShifts" :key="restaurantGroup.id" class="group-section">
            <div class="group-title">{{ restaurantGroup.name }}</div>

            <div class="d-flex">
              <div v-for="col in calendarColumns" :key="col.date" class="calendar-cell"
                :class="{ 'calendar-cell--today': col.isToday }">
                <div class="d-flex align-center mb-1">
                  <!-- Add Shift Button -->
                  <v-btn x-small text class="add-shift-btn" @click="openNewShiftModal(restaurantGroup, col.date)">
                    <v-icon x-small left>mdi-plus</v-icon>
                    Add Shift
                  </v-btn>
                  <!-- PASTE SHIFT -->
                  <v-btn v-if="copiedShift" x-small text color="primary" class="add-shift-btn ml-1"
                    @click="pasteShift(restaurantGroup, col.date)">
                    <v-icon x-small left>mdi-content-paste</v-icon>
                    Paste
                  </v-btn>
                  <v-btn v-if="copiedShift" x-small icon @click="copiedShift = null">
                    <v-icon x-small color="red">
                      mdi-close
                    </v-icon>
                  </v-btn>
                </div>

                <!-- Shifts for this cell -->
                <div v-for="shift in getShiftsForDay(restaurantGroup, col.date)" :key="shift.id" class="shift-card"
                  :class="{ 'shift-card--published': Number(shift.status) === 20 }"
                  @click="openDriverShareScheduleActionModal(shift)">
                  <div class="d-flex align-center justify-space-between">
                    <div class="shift-driver">
                      <v-icon x-small class="mr-1" :color="shift.driver ? 'blue darken-1' : 'grey'">mdi-account</v-icon>
                      {{ shift.driver ? shift.driver.first_name : 'Open shift' }}
                    </div>
                    <v-tooltip top v-if="shiftHasOrders(shift)">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon x-small color="#2563eb" v-bind="attrs" v-on="on"
                          class="shift-orders-icon"
                          @click.stop="openOrdersModal(shift)">
                          mdi-eye-circle
                        </v-icon>
                      </template>
                      <span>{{ activeOrderCount(shift) }} active order(s) — click to view</span>
                    </v-tooltip>
                  </div>
                  <div class="shift-time">
                    <v-icon x-small class="mr-1" color="grey">mdi-clock-outline</v-icon>
                    {{ formatTime(shift.driver_start_time) }} – {{ formatTime(shift.driver_finish_time) }}
                  </div>
                  <div v-for="s in shift.shifts" :key="s.id" class="shift-restaurant">
                    <v-icon x-small class="mr-1" color="grey">mdi-store</v-icon>
                    {{ s.restaurant ? s.restaurant.name : '' }}
                    <span class="shift-restaurant-time">{{ formatTime(s.start_time) }}–{{ formatTime(s.finish_time) }}</span>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
        <!-- end calendar-body -->

      </v-col>
    </v-row>

    <!-- New Shift Modal -->
    <new-drivershare-schedule-modal v-if="showNewShiftModal" :selectedDate="selectedDay" :selectedGroup="selectedGroup"
      @close="showNewShiftModal = false" @saved="onShiftSaved" />

    <!-- Shift Action Modal -->
    <drivershare-schedule-action-modal v-if="showShiftActionModal" :shift="selectedShift"
      @close="showShiftActionModal = false" @edit="openEditShiftModal" @copy="copyShift" @delete="deleteShift"
      @publish="publishShift" @unpublish="unpublishShift" @view-orders="openOrdersModal" />

    <!-- Edit Shift Modal -->
    <edit-drivershare-schedule-modal v-if="showEditShiftModal" :shift="selectedShift"
      @close="showEditShiftModal = false" @saved="onShiftSaved" />
    <!-- Orders Show Modal -->
     <drivershare-schedule-orders-modal v-if="showOrdersModal" :shift="selectedShift" @close="showOrdersModal = false"/>

  </v-container>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import * as _ from 'lodash'
import { mapGetters } from 'vuex'
import { TYPE_AREA, TYPE_RESTAURANT } from '@/store/areas'
import {
  DATE_RANGE_HELPERS,
  INTERVAL_DAY,
  INTERVAL_WEEK,
  INTERVAL_TWO_WEEK,
  INTERVAL_MONTH,
} from '@/store/shifts'
const STOP_STATUS_PENDING = 30
const STOP_STATUS_ENROUTE = 35
const STOP_STATUS_COMPLETED = 40
const STOP_STATUS_CANCELLED = 50

export default {
  name: "DriverShareSchedule",

  data() {
    return {
      TYPE_AREA,
      TYPE_RESTAURANT,
      // Initialise to current isoWeek so the prop passed to copy modal is never null
      date_range_selected: [
        moment().startOf('isoWeek').format('YYYY-MM-DD'),
        moment().endOf('isoWeek').format('YYYY-MM-DD'),
      ],
      menu_date_picker: false,
      copiedShift: null,
      search_driver: "",
      selectedDriver: null,
      selectedRestaurantGroupId: null,
      interval_selected_id: INTERVAL_WEEK,
      showNewShiftModal: false,
      showEditShiftModal: false,
      showShiftActionModal: false,
      showOrdersModal: false,
      selectedGroup: null,
      selectedDay: null,
      selectedShift: null,
      showLoader: false,
      interval_list: [
        { id: INTERVAL_DAY, name: 'Day', $isDisabled: true },
        { id: INTERVAL_WEEK, name: 'Week' },
        { id: INTERVAL_TWO_WEEK, name: '2-Week' },
        { id: INTERVAL_MONTH, name: 'Month' },
      ],
    }
  },

  computed: {
    ...mapGetters(['areas', 'drivers', 'restaurantGroups', 'drivershareShifts']),

    driver_list() {
      return this.drivers
    },
    driverListSearched() {
      let driver_list = _.map(this.driver_list, p => p)
      if (this.search_driver) {
        driver_list = _.filter(driver_list, p =>
          p.full_name && p.full_name.toLowerCase().includes(this.search_driver.toLowerCase())
        )
      }
      if (driver_list.length === 0 || (driver_list.length > 0 && driver_list[0].id !== null)) {
        driver_list.unshift({ id: -2, full_name: `Open shifts [${this.openShiftCount}]` })
        driver_list.unshift({ id: -1, full_name: `All drivers [${driver_list.length}]` })
      }
      return driver_list
    },
    openShiftCount() {
      return this.drivershareShifts.filter(s => !s.driver_id).length
    },
    unpublishedShifts() {
      return this.drivershareShifts.filter(s => Number(s.status) !== 20)
    },
    unpublishedCount() {
      return this.unpublishedShifts.length
    },

    calendarColumns() {
      if (!this.date_range_selected || !this.date_range_selected[0]) return []

      const start = moment(this.date_range_selected[0], 'YYYY-MM-DD')
      const end = this.date_range_selected[1]
        ? moment(this.date_range_selected[1], 'YYYY-MM-DD')
        : start.clone()

      const today = moment().format('YYYY-MM-DD')
      const cols = []
      let cur = start.clone()

      while (cur.isSameOrBefore(end, 'day')) {
        const dateStr = cur.format('YYYY-MM-DD')
        cols.push({
          date: dateStr,
          dayName: cur.format('ddd'),
          dateLabel: cur.format('DD/MM/YYYY'),
          isToday: dateStr === today,
        })
        cur.add(1, 'day')
      }

      return cols
    },

    restaurantGroupsWithShifts() {
      const groups = this.selectedRestaurantGroupId ? this.restaurantGroups.filter(g => g.id === this.selectedRestaurantGroupId) : this.restaurantGroups
      return groups.map(group => {
        const shifts = this.drivershareShifts.filter(
          s => s.restaurant_group_id === group.id
        )
        return { ...group, shifts }
      })
    },
  },
    watch: {
      '$store.state.socketObserver.isConnected'(isConnected) {
          if (isConnected) {
              this.$store.dispatch('bindDrivershareSocketEvents')
              console.log('connected');
          }
      },
  },

  mounted() {
    this.$store.dispatch('bindDrivershareSocketEvents')
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
    })

    this.showLoader = true
    Promise.all([
      this.$store.dispatch('fetchLocations'),
      this.$store.dispatch('fetchDrivers'),
      this.$store.dispatch('fetchRestaurantGroups'),
    ]).then(() => {
      const startDate = moment().startOf('isoWeek').format('YYYY-MM-DD')
      this.fetchShiftsForRange(startDate)
    })
  },

  beforeDestroy() {
    this.$store.dispatch('unbindDrivershareSocketEvents')
    window.removeEventListener('resize', this.onResize)
  },

  methods: {
    orderHasActiveStops(order, shift) {
      const stops = Array.isArray(order.stops) ? order.stops : []
      const shiftStops = stops.filter(s => Number(s.shift_id) === Number(shift.id))
      // Fall back to all stops on the order if none are tagged with this shift's id
      const relevantStops = shiftStops.length > 0 ? shiftStops : stops
      return relevantStops.some(s => [STOP_STATUS_PENDING, STOP_STATUS_ENROUTE].includes(Number(s.status)))
    },
    shiftHasOrders(shift) {
      if (!Array.isArray(shift.orders) || shift.orders.length === 0) return false
      return shift.orders.some(order => this.orderHasActiveStops(order, shift))
    },
    // Count of orders on this shift that still have an active (pending/en route) stop
    activeOrderCount(shift) {
      if (!Array.isArray(shift.orders)) return 0
      return shift.orders.filter(order => this.orderHasActiveStops(order, shift)).length
    },
    dateRangeSelect() {
      if (!this.date_range_selected || !this.date_range_selected[0]) return
      this.fetchShiftsForRange(this.date_range_selected[0])
    },
    fetchShiftsForRange(startDate) {
      const range = DATE_RANGE_HELPERS.dayToRange(startDate, this.interval_selected_id)
      if (!range || !range[0] || !range[1]) {
        console.warn('[Schedule] dayToRange returned bad range:', range)
        return
      }

      Vue.set(this, 'date_range_selected', [range[0], range[1]])

      this.showLoader = true
      this.$store.dispatch('fetchDrivershareShifts', {
        period_date_from: range[0],
        period_date_to: range[1],
        with_timesheet: '1',
        with_orders: '1',
        with_routes: '1',
        driver: '1',
      }).then(() => {
      }).finally(() => {
        this.showLoader = false
      })
    },
    onResize() {
      this.scroll_panel_height = window.innerHeight - 122
    },
    onSelectInterval() {
      let startDate = moment(this.date_range_selected[0], 'YYYY-MM-DD')
      if ([INTERVAL_WEEK, INTERVAL_TWO_WEEK, INTERVAL_MONTH].includes(this.interval_selected_id)) {
        startDate = startDate.startOf('isoWeek')
      }
      if (this.interval_selected_id === INTERVAL_DAY) {
        startDate = moment()
      }
      this.fetchShiftsForRange(startDate.format('YYYY-MM-DD'))
    },
    onPreviosRange() {
      let date = moment(this.date_range_selected[0], 'YYYY-MM-DD')
      switch (this.interval_selected_id) {
        case INTERVAL_DAY: date.subtract(1, 'days'); break
        case INTERVAL_WEEK: date.subtract(1, 'weeks'); break
        case INTERVAL_TWO_WEEK: date.subtract(2, 'weeks'); break
        case INTERVAL_MONTH: date.subtract(1, 'months'); break
      }
      this.fetchShiftsForRange(date.format('YYYY-MM-DD'))
    },
    onNextRange() {
      let date = moment(this.date_range_selected[0], 'YYYY-MM-DD')
      switch (this.interval_selected_id) {
        case INTERVAL_DAY: date.add(1, 'days'); break
        case INTERVAL_WEEK: date.add(1, 'weeks'); break
        case INTERVAL_TWO_WEEK: date.add(2, 'weeks'); break
        case INTERVAL_MONTH: date.add(1, 'months'); break
      }
      this.fetchShiftsForRange(date.format('YYYY-MM-DD'))
    },
    dayToRange(date, interval_id) {
      return DATE_RANGE_HELPERS.dayToRange(date, interval_id)
    },
    dateRangeText(date_range_selected) {
      return DATE_RANGE_HELPERS.dateRangeText(date_range_selected)
    },
    getShiftsForDay(group, date) {
      let shifts = group.shifts.filter(s =>
      moment(s.period_date).format('YYYY-MM-DD') === date
      )
      // nothing selected → show everything
      if (!this.selectedDriver) {
        return shifts
      }
      // All drivers clicked → show everything
      if (this.selectedDriver.id === -1) {
        return shifts
      }
      // Open shifts clicked → only shifts with no driver
      if (this.selectedDriver.id === -2) {
        return shifts.filter(s => !s.driver_id)
      }
      // Specific driver clicked
      return shifts.filter(
        s => Number(s.driver_id) === Number(this.selectedDriver.id)
      )
    },
    toggleDriverFilter(driver) {
      if (
        driver.id === -2 &&
        this.selectedDriver &&
        this.selectedDriver.id === -2
      ) {
        this.selectedDriver = null
        return
      }
      this.selectedDriver = driver
    },
    openNewShiftModal(restaurantGroup, date) {
      this.selectedGroup = restaurantGroup
      this.selectedDay = date
      this.showNewShiftModal = true
    },
    openEditShiftModal(shift) {
      this.selectedShift = shift
      this.showShiftActionModal = false
      this.$nextTick(() => {
        this.showEditShiftModal = true
      })
    },
    openDriverShareScheduleActionModal(shift) {
      this.selectedShift = shift
      this.showShiftActionModal = true
    },
    openOrdersModal(shift) {
        this.showShiftActionModal = false
        this.selectedShift = shift
        this.$nextTick(() => {
            this.showOrdersModal = true
        })
    },
    deleteShift(shift) {
      this.showShiftActionModal = false
      this.$store.dispatch("deleteDrivershareShift", shift.id)
        .then(() => this.fetchShiftsForRange(this.date_range_selected[0]))
    },
    publishShift(shift) {
      this.showShiftActionModal = false
      this.$store.dispatch('publishDrivershareShifts', [shift.id])
        .then(() => this.fetchShiftsForRange(this.date_range_selected[0]))
        .catch(e => console.error('[Schedule] publishShift error:', e))
    },
    unpublishShift(shift) {
      this.showShiftActionModal = false
      this.$store.dispatch('unpublishDrivershareShifts', [shift.id])
        .then(() => this.fetchShiftsForRange(this.date_range_selected[0]))
        .catch(e => console.error('[Schedule] unpublishShift error:', e))
    },
    onShiftSaved() {
      this.showNewShiftModal = false
      this.showEditShiftModal = false
      this.fetchShiftsForRange(this.date_range_selected[0])
    },
    formatTime(timeStr) {
      if (!timeStr) return ''
      return timeStr.slice(0, 5)
    },
    publishAllShifts() {
      if (!this.unpublishedShifts.length) return
      const ids = this.unpublishedShifts.map(s => s.id)
      this.$store.dispatch('publishDrivershareShifts', ids)
        .then(() => this.fetchShiftsForRange(this.date_range_selected[0]))
        .catch(e => console.error('[Schedule] publishAllShifts error:', e))
    },
    onCopyCompleted() {
      this.fetchShiftsForRange(this.date_range_selected[0])
    },
    copyShift(shift) {
      this.copiedShift = _.cloneDeep(shift)
    },
    pasteShift(restaurantGroup, date) {
      if (!this.copiedShift) return
      const copied = this.copiedShift
      const payload = {
        restaurant_group_id: restaurantGroup.id,
        period_date: date,
        driver_id: copied.driver_id,
        driver_start_time: copied.driver_start_time,
        driver_finish_time: copied.driver_finish_time,
        comment: copied.comment,
        restaurants: copied.shifts.map(s => ({
          restaurant_id: s.restaurant_id,
          start_time: s.start_time
            ? s.start_time.slice(0, 5)
            : null,
          finish_time: s.finish_time
            ? s.finish_time.slice(0, 5)
            : null,
          cost_per_hour: s.cost_per_hour,
          cost_total: s.cost_total,
          meal_time: s.meal_time || 0,
          rest_time: s.rest_time || 0,
          comment: s.comment,
        })),
      }
      this.$store.dispatch('createDrivershareShift', payload)
        .then(() => {
          this.fetchShiftsForRange(this.date_range_selected[0])
        })
    },
  },
}
</script>

<style scoped>
.driver-share-wrapper {
  height: calc(100vh - 64px);
  background: #f5f7fa;
}

.fill-height {
  height: 100%;
}

/* ============= LEFT PANEL ============= */
.left-panel-custom {
  max-width: 230px;
  background: #ffffff;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  height: 100%;
}

/* ============= RIGHT PANEL ============= */
.right-panel-custom {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.top-toolbar {
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  min-height: 64px;
  flex: none;
  margin-top: 10px;
}

.date-pill {
  background: #e5e7eb;
  border-radius: 999px;
  padding: 4px 12px;
}

.interval-select {
  max-width: 140px;
  border-radius: 999px;
}

.interval-select .v-input__control {
  border-radius: 999px !important;
}
.location-autocomplete {
  max-width: 230px;
}
.select-drop-item-sub_restaurant {
  padding-left: 4px;
}

.calendar-header {
  display: flex;
  background: #fafafa;
  border-bottom: 1px solid #ddd;
  flex-shrink: 0;
}

.calendar-day {
  flex: 1;
  padding: 8px 12px;
  border-right: 1px solid #eee;
  min-width: 150px;
}

.calendar-day-name {
  font-weight: 600;
  font-size: 13px;
  color: #374151;
}

.calendar-day-date {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.calendar-day--today .calendar-day-name {
  color: #1d4ed8;
}

.calendar-day--today .calendar-day-date {
  color: #3b82f6;
  font-weight: 600;
}

.calendar-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
}

.group-section {
  margin-bottom: 0;
}

.group-title {
  background: #dfeff5;
  padding: 10px 16px;
  font-weight: 600;
  font-size: 13px;
  border-top: 1px solid #d0e3ea;
  border-bottom: 1px solid #d0e3ea;
  position: sticky;
  top: 0;
  z-index: 1;
}

.group-section .d-flex {
  border-bottom: 1px solid #e5e7eb;
}

.calendar-cell {
  flex: 1;
  min-width: 150px;
  min-height: 120px;
  border-right: 1px solid #eee;
  padding: 8px;
  background: #ffffff;
  vertical-align: top;
}

.calendar-cell--today {
  background: #eff6ff;
}

.add-shift-btn {
  font-size: 11px !important;
  color: #6b7280 !important;
  padding: 0 4px !important;
  height: 22px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.add-shift-btn:hover {
  color: #2563eb !important;
  background: #eff6ff !important;
}

.shift-card {
  background: #e3f2fd;
  border-radius: 6px;
  padding: 6px 8px;
  margin-bottom: 6px;
  font-size: 12px;
  cursor: pointer;
  border-left: 3px solid #60a5fa;
  transition: background 0.15s ease;
}

.shift-card:hover {
  background: #d4ecff;
}

.shift-driver {
  font-weight: 600;
  font-size: 12px;
  color: #1e3a5f;
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}

.shift-time {
  font-size: 11px;
  color: #555;
  display: flex;
  align-items: center;
  margin-bottom: 3px;
}

.shift-restaurant {
  font-size: 11px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shift-restaurant-time {
  color: #9ca3af;
  margin-left: auto;
  flex-shrink: 0;
}

.shift-card--published {
  border-left: 3px solid #16a34a !important;
  background: #f0fdf4 !important;
}

.shift-card--published:hover {
  background: #dcfce7 !important;
}
</style>